import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ProfileSection = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: '',
    role: '',
    email: '',
    accountId: ''
  });
  const [editData, setEditData] = useState({ ...profileData });
  const fileInputRef = useRef(null);

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      // API call to save profile data
      await new Promise(resolve => setTimeout(resolve, 1000));
      setProfileData({ ...editData });
      setIsEditing(false);
      // Show success message
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  const handleCancel = () => {
    setEditData({ ...profileData });
    setIsEditing(false);
  };

  const handlePhotoUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Handle photo upload
      await new Promise(resolve => setTimeout(resolve, 2000));
      // Handle successful upload
    } catch (error) {
      console.error('Failed to upload photo:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileUpload = () => {
    fileInputRef?.current?.click();
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Photo Section */}
        <div className="lg:col-span-1">
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <div className="w-24 h-24 bg-blue-500/20 rounded-full flex items-center justify-center border-2 border-white/15">
                {isUploading ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                ) : (
                  <Icon name="User" size={32} color="#60A5FA" />
                )}
              </div>
              {isEditing && (
                <button
                  onClick={triggerFileUpload}
                  disabled={isUploading}
                  className="absolute -bottom-1 -right-1 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-500 transition-colors disabled:opacity-50"
                >
                  <Icon name="Camera" size={14} />
                </button>
              )}
            </div>
            <div className="text-center">
              <h3 className="font-medium text-white">{profileData?.fullName || 'Full Name'}</h3>
              <p className="text-sm text-slate-300">{profileData?.role || 'Role/Title'}</p>
            </div>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm hover:bg-white/20 transition"
              >
                Edit Profile
              </button>
            )}
            {isEditing && (
              <button
                onClick={triggerFileUpload}
                disabled={isUploading}
                className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm hover:bg-white/20 transition"
              >
                {isUploading ? 'Uploading...' : 'Change Photo'}
              </button>
            )}
          </div>
        </div>

        {/* Profile Form */}
        <div className="lg:col-span-2">
          <div className="space-y-5">
            {/* Full Name */}
            <div>
              {isEditing ? (
                <Input
                  label="Full Name"
                  type="text"
                  value={editData?.fullName}
                  onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                  required
                />
              ) : (
                <div>
                  <label className="text-sm font-medium text-white/60">Full Name</label>
                  <p className="mt-1 text-sm text-white">{profileData?.fullName || 'Not set'}</p>
                </div>
              )}
            </div>

            {/* Role/Title */}
            <div>
              {isEditing ? (
                <Input
                  label="Role/Title"
                  type="text"
                  value={editData?.role}
                  onChange={(e) => handleInputChange('role', e?.target?.value)}
                />
              ) : (
                <div>
                  <label className="text-sm font-medium text-white/60">Role/Title</label>
                  <p className="mt-1 text-sm text-white">{profileData?.role || 'Not set'}</p>
                </div>
              )}
            </div>

            {/* Email Address */}
            <div>
              {isEditing ? (
                <Input
                  label="Email Address"
                  type="email"
                  value={editData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  required
                />
              ) : (
                <div>
                  <label className="text-sm font-medium text-white/60">Email Address</label>
                  <p className="mt-1 text-sm text-white">{profileData?.email || 'Not set'}</p>
                </div>
              )}
            </div>

            {/* Account ID (Read-only) */}
            <div>
              <label className="text-sm font-medium text-white/60">Account ID</label>
              <p className="mt-1 text-sm text-slate-300 font-mono">{profileData?.accountId || 'Not generated'}</p>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex items-center space-x-3 pt-4 border-t border-white/10">
                <button
                  onClick={handleSave}
                  className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition"
                >
                  Save Changes
                </button>
                <button
                  onClick={handleCancel}
                  className="px-5 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm hover:bg-white/20 transition"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoUpload}
        accept="image/*"
        className="hidden"
      />
    </div>
  );
};

export default ProfileSection;